/**
 * 📸 PROFILE PICTURE APPROVAL SERVICE
 * Manages driver profile picture submission and approval workflow
 * Backend: https://polarride-api-17dr.vercel.app/api/v1/admin/driver/profile-picture
 */

import { getApiBaseUrl } from '@/config/environment';


export interface ProfilePictureSubmission {
  id: string;
  userId?: string;
  driverId: string;
  driverName: string;
  driverCode?: string;
  newPicture: string; // Base64 data URL: "data:image/jpeg;base64,..."
  currentPicture?: string | null; // Current profile picture URL or null
  status?: 'pending' | 'approved' | 'rejected';
  submittedAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  rejectionReason?: string;
  canResubmit?: boolean;
}

export interface PendingSubmissionsResponse {
  success: boolean;
  data?: {
    submissions: ProfilePictureSubmission[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
  error?: string;
}

export interface ReviewResponse {
  success: boolean;
  data?: {
    submission: ProfilePictureSubmission;
    message: string;
  };
  error?: string;
}

class ProfilePictureApprovalService {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${getApiBaseUrl()}${endpoint}`;
    const token = localStorage.getItem('token');

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * Get all pending profile picture submissions
   */
  async getPendingSubmissions(page = 1, limit = 20): Promise<PendingSubmissionsResponse> {
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());

      const response = await this.request<PendingSubmissionsResponse>(
        `/api/v1/admin/driver/profile-picture/pending?${params.toString()}`
      );

      return response;
    } catch (error: any) {
      console.error('Failed to get pending submissions:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Approve a profile picture submission
   */
  async approveSubmission(submissionId: string): Promise<ReviewResponse> {
    try {
      const response = await this.request<ReviewResponse>(
        `/api/v1/admin/driver/profile-picture/${submissionId}/review`,
        {
          method: 'PUT',
          body: JSON.stringify({
            action: 'approve'
          }),
        }
      );

      return response;
    } catch (error: any) {
      console.error('Failed to approve submission:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Reject a profile picture submission
   */
  async rejectSubmission(submissionId: string, reason: string): Promise<ReviewResponse> {
    try {
      const response = await this.request<ReviewResponse>(
        `/api/v1/admin/driver/profile-picture/${submissionId}/review`,
        {
          method: 'PUT',
          body: JSON.stringify({
            action: 'reject',
            rejectionReason: reason
          }),
        }
      );

      return response;
    } catch (error: any) {
      console.error('Failed to reject submission:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get count of pending submissions (for badge display)
   */
  async getPendingCount(): Promise<number> {
    try {
      const response = await this.getPendingSubmissions(1, 1);
      return response.data?.pagination?.total || 0;
    } catch (error) {
      console.error('Failed to get pending count:', error);
      return 0;
    }
  }
}

export default new ProfilePictureApprovalService();
